import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Loader } from '@progress/kendo-react-indicators';
import { Checkbox, TextBox, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import React from 'react';
import { useMultiWindowContext } from '../../../app/components/multi-window/context/multi-window-context';
import { useAppDispatch, useAppSelector } from '../../../app/hooks/store-hooks';
import { NewRouteModel, routeAnalyzerAddRoutes } from '../../../app/state/slices/route-analyzer-slice';
import routeApi, { ApiServerRoute, GetApiRoutesType } from '../../../services/http/route-api';
import { AnalyzerRouteType } from '../types';
import { ImportRoutesProps } from './FpmImportRoutes';

export function DatabaseImportRoutes(props: ImportRoutesProps) {
    const tutorialActive = !!useAppSelector((x) => x.global.tutorial);
    const [origin, setOrigin] = React.useState('');
    const [destination, setDestination] = React.useState('');
    //const [filterCompany, setFilteredCompany] = React.useState(true);
    const [filterPreferred, setFilterPreferred] = React.useState(true);
    const [filterCodedDeparture, setFilterCodedDeparture] = React.useState(true);
    //const [filerSwap, setFilterSwap] = React.useState(true);
    const [error, setError] = React.useState(false);
    const [busy, setBusy] = React.useState(false);
    const multiWindowContext = useMultiWindowContext();
    const dispatch = useAppDispatch();

    const handleOriginChanged = React.useCallback((event: TextBoxChangeEvent) => setOrigin(event.value as string), []);
    const handleDestinationChanged = React.useCallback((event: TextBoxChangeEvent) => setDestination(event.value as string), []);

    const handleCancel = () => {
        props.onClose();
    };

    React.useEffect(() => {
        if (tutorialActive) {
            setOrigin('KJFK');
            setDestination('KLAX');
        }
    }, [tutorialActive]);

    const handleImport = async () => {
        setError(false);
        setBusy(true);

        const type: GetApiRoutesType =
            (filterPreferred ? GetApiRoutesType.preferred : 0) | (filterCodedDeparture ? GetApiRoutesType.codedDeparture : 0);

        const routes = await routeApi.getRoutesForPair(origin, destination, type);
        if (!routes) {
            setError(true);
            setBusy(false);
            return;
        }

        const toModel = (input: ApiServerRoute[], type: AnalyzerRouteType): NewRouteModel[] => {
            return input.map((item) => ({
                type: type,
                label: item.label,
                origin: item.originID,
                destination: item.destID,
                sequence: item.seq,
                distance: item.dst ?? null,
                ete: item.ete ?? null,
                time: null,
                route: item.route.rtxt,
                waypoints: { items: item.route.wp },
                associatedPoints: [],
                feedId: item.route.fd,
                visible: false
            }));
        };

        const result = [...toModel(routes.preferred, AnalyzerRouteType.preferred), ...toModel(routes.codedRoutes, AnalyzerRouteType.cdr)];
        dispatch(routeAnalyzerAddRoutes({ routes: result }));

        props.onClose();
    };

    const importEnabled = origin && destination && (filterCodedDeparture || filterPreferred);

    return (
        <Dialog width={400} title={'Import from database'} onClose={handleCancel} appendTo={multiWindowContext.root}>
            <div className="d-flex gap-2 flex-column pb-2" id="route-analyzer-database-import">
                <TextBox
                    className="input-uppercase"
                    placeholder="Origin (optional)"
                    value={origin}
                    onChange={handleOriginChanged}></TextBox>
                <TextBox
                    className="input-uppercase"
                    placeholder="Destination (optional)"
                    value={destination}
                    onChange={handleDestinationChanged}></TextBox>
                <div>
                    {/* <Checkbox label={'Company'} value={filterCompany} onChange={e => setFilteredCompany(e.value)}></Checkbox> */}
                    <Checkbox label={'Preferred'} value={filterPreferred} onChange={(e) => setFilterPreferred(e.value)}></Checkbox>
                    <Checkbox
                        label={'Coded departure'}
                        value={filterCodedDeparture}
                        onChange={(e) => setFilterCodedDeparture(e.value)}></Checkbox>
                    {/* <Checkbox label={'SWAP'} value={filerSwap} onChange={e => setFilterSwap(e.value)}></Checkbox> */}
                </div>
                {error && <div className="text-danger">Could not import routes</div>}
                {busy && <Loader type="infinite-spinner" className="mx-auto" />}
            </div>
            <DialogActionsBar>
                <div className="d-flex gap-2">
                    <Button className="flex-grow-1" fillMode={'outline'} onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button
                        className="flex-grow-1"
                        themeColor={'primary'}
                        onClick={handleImport}
                        disabled={!importEnabled}
                        id="route-analyzer-database-import-btn">
                        Import
                    </Button>
                </div>
            </DialogActionsBar>
        </Dialog>
    );
}
